if (!customElements.get('live-atomic-ugc')) {
  customElements.define('live-atomic-ugc', class extends HTMLElement {
    connectedCallback() {
      if (!this.initialized) {
        this.initialized = true;
        this.rail = this.querySelector('[data-ugc-rail]');

        this.addEventListener('click', (event) => this.onClick(event));
        this.visibilityHandler = () => {
          if (document.hidden) this.pauseVideos();
        };

        this.arrows = this.querySelector('.la-ugc__arrows');
        this.previous = this.querySelector('[data-ugc-previous]');
        this.next = this.querySelector('[data-ugc-next]');
        if (this.rail && this.arrows) {
          this.rail.addEventListener('scroll', () => this.scheduleArrowUpdate(), { passive: true });
          if ('ResizeObserver' in window) {
            new ResizeObserver(() => this.scheduleArrowUpdate()).observe(this.rail);
          } else {
            window.addEventListener('resize', () => this.scheduleArrowUpdate());
          }
          this.updateArrows();
        }
      }

      if (this.connected) return;
      this.connected = true;
      document.addEventListener('visibilitychange', this.visibilityHandler);
      this.connectVideoObserver();
    }

    disconnectedCallback() {
      if (!this.connected) return;
      this.connected = false;
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.videoObserver?.disconnect();
      this.videoObserver = null;
      this.pauseVideos();
    }

    connectVideoObserver() {
      if (!('IntersectionObserver' in window)) return;
      this.videoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target.querySelector('video');
          if (!entry.isIntersecting && video && !video.paused) video.pause();
        });
      }, { root: this.rail, threshold: 0.25 });
      this.querySelectorAll('.la-ugc__card').forEach((card) => {
        if (card.querySelector('.la-ugc__video')) this.videoObserver.observe(card);
      });
    }

    scheduleArrowUpdate() {
      if (this.arrowFrame) return;
      this.arrowFrame = requestAnimationFrame(() => {
        this.arrowFrame = 0;
        this.updateArrows();
      });
    }

    updateArrows() {
      const rail = this.rail;
      const maxScroll = rail.scrollWidth - rail.clientWidth;
      const scrollable = maxScroll > 2;
      this.arrows.hidden = !scrollable;
      if (!scrollable) return;
      const position = rail.scrollLeft;
      if (this.previous) this.previous.disabled = position <= 2;
      if (this.next) this.next.disabled = position >= maxScroll - 2;
    }

    scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    scrollStep() {
      const card = this.rail.querySelector('.la-ugc__card');
      const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
      return (card?.offsetWidth || 280) + gap;
    }

    onClick(event) {
      const mute = event.target.closest('[data-ugc-mute]');
      if (mute) {
        const video = mute.closest('.la-ugc__media-shell')?.querySelector('.la-ugc__video');
        if (video) video.muted = !video.muted;
        return;
      }

      const toggle = event.target.closest('[data-ugc-toggle], [data-ugc-control-toggle]');
      if (toggle) {
        const video = toggle.closest('.la-ugc__media-shell')?.querySelector('.la-ugc__video');
        if (video) this.toggleVideo(video);
        return;
      }

      const play = event.target.closest('[data-ugc-play]');
      if (play) {
        this.openMedia(play);
        return;
      }

      const direction = event.target.closest('[data-ugc-previous]') ? -1 : event.target.closest('[data-ugc-next]') ? 1 : 0;
      if (direction && this.rail) {
        this.rail.scrollBy({ left: direction * this.scrollStep(), behavior: this.scrollBehavior() });
      }
    }

    openMedia(button) {
      const shell = button.closest('.la-ugc__media-shell');
      const existingVideo = shell?.querySelector('.la-ugc__video');
      if (existingVideo) {
        this.toggleVideo(existingVideo);
        return;
      }

      const template = shell?.querySelector('template[data-ugc-media]');
      if (!template) return;

      this.pauseVideos();
      const media = template.content.cloneNode(true);
      this.activateSources(media);
      const target = shell.querySelector('[data-ugc-inline]');
      target.replaceChildren(media);
      const moveFocus = button.matches(':focus-visible');
      button.hidden = true;

      const video = target.querySelector('.la-ugc__video');
      this.prepareVideo(video);
      this.keepCardVisible(button.closest('.la-ugc__card'));
      video.load();
      video.play().catch(() => this.updateVideoState(video));
      if (moveFocus) video.focus();
    }

    activateSources(root) {
      root.querySelectorAll('[data-src]').forEach((node) => {
        node.src = node.dataset.src;
        node.removeAttribute('data-src');
      });
    }

    prepareVideo(video) {
      if (!video) return;
      const card = video.closest('.la-ugc__card');
      this.videoObserver?.observe(card);
      video.addEventListener('play', () => {
        this.pauseVideos(video);
        this.updateVideoState(video);
      });
      video.addEventListener('playing', () => this.enableCustomControls(video), { once: true });
      video.addEventListener('pause', () => this.updateVideoState(video));
      video.addEventListener('ended', () => this.updateVideoState(video));
      video.addEventListener('volumechange', () => this.updateVideoState(video));
    }

    toggleVideo(video) {
      if (video.paused || video.ended) {
        if (video.ended) video.currentTime = 0;
        video.play().catch(() => this.updateVideoState(video));
      } else {
        video.pause();
      }
    }

    updateVideoState(video) {
      const playing = !video.paused && !video.ended;
      const shell = video.closest('.la-ugc__media-shell');
      shell?.closest('.la-ugc__card')?.classList.toggle('is-playing', playing);
      video.dataset.playbackState = playing ? 'playing' : 'paused';
      const title = video.dataset.title || 'video';
      const overlay = shell?.querySelector('[data-ugc-toggle]');
      const toggle = shell?.querySelector('[data-ugc-control-toggle]');
      const mute = shell?.querySelector('[data-ugc-mute]');
      if (overlay) overlay.setAttribute('aria-label', `${playing ? 'Pause' : 'Play'} ${title}`);
      if (toggle) {
        toggle.textContent = playing ? 'Pause' : 'Play';
        toggle.setAttribute('aria-label', `${playing ? 'Pause' : 'Play'} ${title}`);
      }
      if (mute) {
        mute.textContent = video.muted ? 'Unmute' : 'Mute';
        mute.setAttribute('aria-label', `${video.muted ? 'Unmute' : 'Mute'} ${title}`);
        mute.setAttribute('aria-pressed', String(video.muted));
      }
    }

    enableCustomControls(video) {
      const shell = video.closest('.la-ugc__media-shell');
      const moveFocus = document.activeElement === video;
      video.controls = false;
      shell?.querySelectorAll('[data-ugc-toggle], [data-ugc-controls]').forEach((node) => { node.hidden = false; });
      this.updateVideoState(video);
      if (moveFocus) shell?.querySelector('[data-ugc-control-toggle]')?.focus();
    }

    pauseVideos(except) {
      this.querySelectorAll('.la-ugc__video').forEach((video) => {
        if (video !== except && !video.paused) video.pause();
      });
    }

    keepCardVisible(card) {
      if (!card || !this.rail) return;
      const railRect = this.rail.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const buffer = 18;
      let distance = 0;
      if (cardRect.left < railRect.left + buffer) distance = cardRect.left - railRect.left - buffer;
      else if (cardRect.right > railRect.right - buffer) distance = cardRect.right - railRect.right + buffer;
      if (distance) this.rail.scrollBy({ left: distance, behavior: this.scrollBehavior() });
    }
  });
}
